import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { playerAvatar } from "../lib/api";
import AvatarLayered from "./AvatarLayered";
import { PWChatCard, isPWCardKind } from "./PromotionWars";
import { useAuth } from "../lib/auth";
import { Send, MessageCircle, Filter, Shield, Megaphone, Users as UsersIcon } from "lucide-react";
import { toast } from "sonner";
import { visibleInterval } from "../lib/visibleInterval";

function formatTime(iso) {
  const d = new Date(iso);
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
}

const TITLE_IMG = {
  heavyweight: "/pvp/title_heavyweight.webp",
  middleweight: "/pvp/title_middleweight.webp",
  cruiserweight: "/pvp/title_cruiserweight.webp",
  tag_team: "/pvp/title_tag_team.webp",
};
const DIV_LABEL = {
  heavyweight: "HEAVYWEIGHT",
  middleweight: "MIDDLEWEIGHT",
  cruiserweight: "CRUISERWEIGHT",
  tag_team: "TAG TEAM",
};

// Escape regex special chars in a mention name
function escapeRegex(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** Renders a chat message body, replacing mention names with clickable spans. */
function MessageBody({ text, mentions, onOpenProfile }) {
  if (!mentions || mentions.length === 0) {
    return <span className="text-[#E5DCFF] break-words">{text}</span>;
  }
  // Sort mentions by name length DESC so overlapping names ("Aces" vs "Ace") are handled correctly
  const sorted = [...mentions].filter((m) => m && m.name).sort((a, b) => b.name.length - a.name.length);
  const pattern = new RegExp(`(${sorted.map((m) => escapeRegex(m.name)).join("|")})`, "g");
  const parts = text.split(pattern);
  return (
    <span className="text-[#A89BCC] break-words">
      {parts.map((part, i) => {
        const hit = sorted.find((m) => m.name === part);
        if (!hit) return <React.Fragment key={i}>{part}</React.Fragment>;
        if (!hit.account_name) {
          return (
            <span key={i} className="font-heading uppercase tracking-wider" style={{ color: "#FFE600", textShadow: "0 0 6px #FFE60077" }}>
              {part}
            </span>
          );
        }
        return (
          <button
            key={i}
            type="button"
            data-testid={`chat-mention-${hit.account_name}`}
            onClick={() => onOpenProfile(hit.account_name)}
            className="font-heading uppercase tracking-wider hover:underline underline-offset-2 cursor-pointer transition-colors"
            style={{ color: "#FFE600", textShadow: "0 0 6px #FFE60077" }}
            title={`View ${hit.account_name}`}
          >
            {part}
          </button>
        );
      })}
    </span>
  );
}

/** Rich card layout for a title_shot system message. */
function TitleShotCard({ m, onOpenProfile }) {
  const meta = m.meta || {};
  const division = meta.division;
  const tag = meta.result_tag;
  const tagLabel = tag === "UPSET" ? "UPSET WIN" : tag === "DECISIVE" ? "DECISIVE VICTORY" : "EVENLY MATCHED";
  const tagColor = tag === "UPSET" ? "#FFE600" : tag === "DECISIVE" ? "#39FF14" : "#00F0FF";
  const isTag = division === "tag_team";

  // ---- Tag team title layout: show both member avatars + team names for winner and loser ----
  if (isTag) {
    const winnerMembers = meta.winner_members || [];
    const loserMembers = meta.loser_members || [];
    const wTeamName = meta.winner_team_name || "Winner";
    const lTeamName = meta.loser_team_name;
    const stackAvatars = (members, borderColor) => (
      <div className="flex items-center -space-x-2 flex-shrink-0">
        {members.map((mm, i) => (
          <button
            key={mm.wrestler_id || i}
            type="button"
            data-testid={`chat-team-avatar-${m.msg_id}-${i}`}
            onClick={() => mm.account_name && onOpenProfile(mm.account_name)}
            title={mm.account_name ? `View ${mm.account_name}` : ""}
            className="focus:outline-none"
            style={{ zIndex: members.length - i }}
          >
            <AvatarLayered showCrown
              fgSrc={`${api.defaults.baseURL}/wrestlers/${mm.wrestler_id}/avatar?v=${new Date(mm.avatar_updated_at || 0).getTime()}`}
              promo={mm.promotion}
              className="w-11 h-11 rounded-md border-2 cursor-pointer hover:scale-110 transition-transform"
              style={{ borderColor, boxShadow: `0 0 8px ${borderColor}88` }}
            />
          </button>
        ))}
      </div>
    );
    return (
      <div
        data-testid={`chat-title-shot-${m.msg_id}`}
        className="my-1 p-2.5 rounded-lg border flex items-center gap-2.5"
        style={{
          borderColor: "#FFE60088",
          background: "linear-gradient(135deg, rgba(255,230,0,0.10), rgba(255,0,127,0.05))",
          boxShadow: "0 0 14px rgba(255,230,0,0.18)",
        }}
      >
        <img
          src={TITLE_IMG.tag_team || TITLE_IMG.heavyweight}
          alt="tag_team"
          className="w-12 h-12 rounded-md object-cover flex-shrink-0"
          style={{ boxShadow: `0 0 10px ${tagColor}77` }}
        />
        {stackAvatars(winnerMembers, "#FFE600")}
        <div className="min-w-0 flex-1">
          <div className="font-heading text-xs uppercase tracking-widest" style={{ color: "#FFE600", textShadow: "0 0 6px #FFE60077" }}>
            🏆 TAG TEAM CHAMPIONSHIP
          </div>
          <div className="font-heading text-sm mt-0.5">
            <span className="text-[#39FF14] mr-1" data-testid={`chat-winner-team-${m.msg_id}`}>{wTeamName}</span>
            {meta.vacant ? (
              <span className="text-[#A89BCC]">claim the vacant title!</span>
            ) : lTeamName ? (
              <>
                <span className="text-[#A89BCC]">beat </span>
                <span className="text-[#FF007F]" data-testid={`chat-loser-team-${m.msg_id}`}>{lTeamName}</span>
                <span className="text-[#A89BCC]"> for the title!</span>
              </>
            ) : (
              <span className="text-[#A89BCC]">win the title!</span>
            )}
          </div>
          <div className="text-[10px] font-heading uppercase tracking-widest mt-0.5" style={{ color: tagColor }}>{tagLabel}</div>
        </div>
        {loserMembers.length > 0 && stackAvatars(loserMembers, "#FF007F")}
      </div>
    );
  }

  // ---- Singles title layout (unchanged) ----
  const winner = (m.mentions || []).find((x) => x.wrestler_id === meta.winner_wrestler_id) || (m.mentions || [])[0];
  const loser = meta.loser_wrestler_id ? (m.mentions || []).find((x) => x.wrestler_id === meta.loser_wrestler_id) : null;
  return (
    <div
      data-testid={`chat-title-shot-${m.msg_id}`}
      className="my-1 p-2.5 rounded-lg border flex items-center gap-3"
      style={{
        borderColor: "#FFE60088",
        background: "linear-gradient(135deg, rgba(255,230,0,0.10), rgba(255,0,127,0.05))",
        boxShadow: "0 0 14px rgba(255,230,0,0.18)",
      }}
    >
      <img
        src={TITLE_IMG[division] || TITLE_IMG.heavyweight}
        alt={division}
        className="w-12 h-12 rounded-md object-cover flex-shrink-0"
        style={{ boxShadow: `0 0 10px ${tagColor}77` }}
      />
      {winner?.wrestler_id && (
        <button
          type="button"
          data-testid={`chat-winner-avatar-${m.msg_id}`}
          onClick={() => winner.account_name && onOpenProfile(winner.account_name)}
          className="flex-shrink-0"
          title={winner.account_name ? `View ${winner.account_name}` : ""}
        >
          <AvatarLayered showCrown
            fgSrc={`${api.defaults.baseURL}/wrestlers/${winner.wrestler_id}/avatar?v=${new Date(winner.avatar_updated_at || 0).getTime()}`}
            promo={winner.promotion}
            className="w-14 h-14 rounded-md border-2 cursor-pointer hover:scale-105 transition-transform"
            style={{ borderColor: "#FFE600", boxShadow: "0 0 10px #FFE60088" }}
          />
        </button>
      )}
      <div className="min-w-0 flex-1">
        <div className="font-heading text-xs uppercase tracking-widest" style={{ color: "#FFE600", textShadow: "0 0 6px #FFE60077" }}>
          🏆 {DIV_LABEL[division] || division} CHAMPIONSHIP
        </div>
        <div className="font-heading text-sm mt-0.5">
          {winner?.account_name ? (
            <button
              type="button"
              data-testid={`chat-mention-${winner.account_name}`}
              onClick={() => onOpenProfile(winner.account_name)}
              className="text-[#39FF14] hover:underline underline-offset-2 mr-1"
            >
              {winner.name}
            </button>
          ) : (
            <span className="text-[#39FF14] mr-1">{winner?.name}</span>
          )}
          {meta.vacant ? (
            <span className="text-[#A89BCC]">claims the vacant title!</span>
          ) : loser ? (
            <>
              <span className="text-[#A89BCC]">defeats </span>
              {loser.account_name ? (
                <button
                  type="button"
                  data-testid={`chat-mention-${loser.account_name}`}
                  onClick={() => onOpenProfile(loser.account_name)}
                  className="text-[#FF007F] hover:underline underline-offset-2"
                >
                  {loser.name}
                </button>
              ) : (
                <span className="text-[#FF007F]">{loser.name}</span>
              )}
              <span className="text-[#A89BCC]">
                {meta.retain
                  ? ` to retain the title! (${meta.defenses || 1} defense${(meta.defenses || 1) !== 1 ? "s" : ""})`
                  : " to become champion!"}
              </span>
            </>
          ) : (
            <span className="text-[#A89BCC]">wins the title!</span>
          )}
        </div>
        <div className="text-[10px] font-heading uppercase tracking-widest mt-0.5" style={{ color: tagColor }}>
          {tagLabel}
          {meta.retain && <span className="ml-2 text-[#FFD700]">· TITLE RETAINED</span>}
        </div>
      </div>
    </div>
  );
}

/** Rich highlight card for rumble_win / bank_win system messages. */
function EventWinnerCard({ m, onOpenProfile }) {
  const meta = m.meta || {};
  const kind = meta.kind;
  const isRumble = kind === "rumble_win";
  const color = isRumble ? "#FF007F" : "#FFE600";
  const label = isRumble ? "RUMBLE ROYALE CHAMPION" : "BRIEFCASE SECURED";
  const subtitle = isRumble ? "Title Shot incoming" : "Titleshot In The Bank";
  const wrestlerId = meta.winner_wrestler_id;
  const promoId = meta.winner_promotion_id;
  const fgSrc = wrestlerId ? `${api.defaults.baseURL}/wrestlers/${wrestlerId}/avatar?v=${new Date(meta.winner_avatar_updated_at || 0).getTime()}` : null;
  return (
    <div
      data-testid={`event-winner-card-${kind}`}
      className="rounded-lg p-3 flex items-center gap-3 my-1"
      style={{
        background: `linear-gradient(90deg, ${color}22, rgba(10,5,26,0.9))`,
        border: `2px solid ${color}`,
        boxShadow: `0 0 18px ${color}66`,
      }}
    >
      {fgSrc && (
        <AvatarLayered showCrown
          fgSrc={fgSrc}
          promo={promoId ? { id: promoId, avatar_updated_at: meta.winner_promotion_avatar_updated_at } : null}
          className="w-14 h-14 rounded-md border-2 flex-shrink-0"
          style={{ borderColor: color, boxShadow: `0 0 10px ${color}88` }}
          testId={`event-winner-avatar-${m.msg_id}`}
        />
      )}
      <div className="flex-1 min-w-0">
        <div className="font-heading text-xs uppercase tracking-widest" style={{ color }}>
          {label}
        </div>
        <div className="font-heading text-xl truncate" style={{ color: "#fff", textShadow: `0 0 8px ${color}` }}>
          {meta.winner_account_name ? (
            <button
              type="button"
              data-testid={`event-winner-name-${kind}`}
              onClick={() => onOpenProfile(meta.winner_account_name)}
              className="hover:underline underline-offset-2 cursor-pointer transition-colors"
              title={`View ${meta.winner_account_name}`}
            >
              {meta.winner_name || "Winner"}
            </button>
          ) : (
            <span>{meta.winner_name || "Winner"}</span>
          )}
        </div>
        <div className="text-[10px] font-mono text-[#A89BCC] mt-0.5">
          {subtitle}
          {meta.participants ? ` · ${meta.participants} entrants` : ""}
        </div>
      </div>
    </div>
  );
}

export default function ChatBox({ compact = false }) {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const chatBan = user?.chat_ban && (!user.chat_ban.until || new Date(user.chat_ban.until) > new Date()) ? user.chat_ban : null;
  const [filter, setFilter] = useState("all");  // "all" | "announcer" | "player" | "promotion"
  const [filterOpen, setFilterOpen] = useState(false);
  const scrollRef = useRef(null);
  const inPromotion = !!(user?.promotion?.id);

  const load = async () => {
    try {
      const r = await api.get("/chat");
      setMessages(r.data);
    } catch {}
  };

  useEffect(() => {
    load();
    return visibleInterval(load, 4000);
  }, []);

  const [online, setOnline] = useState(null);
  useEffect(() => {
    const fetchOnline = () => api.get("/players/online-count").then((r) => setOnline(r.data.online)).catch(() => {});
    fetchOnline();
    return visibleInterval(fetchOnline, 30000);
  }, []);

  // Filter messages for display. Base rules:
  //   - all        : show everything.
  //   - announcer  : only system/ring-announcer.
  //   - player     : only non-system, non-promotion messages.
  //   - promotion  : only promotion-scoped messages (already filtered by API to our promo).
  const visible = useMemo(() => {
    return messages.filter((m) => {
      const isSystem = m.system === true || m.user_id === "__system__";
      const scope = m.scope || (isSystem ? "system" : "global");
      if (filter === "all") return true;
      if (filter === "announcer") return isSystem;
      if (filter === "player") return !isSystem && scope !== "promotion";
      if (filter === "promotion") return scope === "promotion";
      return true;
    });
  }, [messages, filter]);

  // Auto-scroll to the newest message whenever the visible list changes OR
  // the last-message id changes (so filter changes + new arrivals both snap
  // the user to the bottom instead of leaving them mid-scroll).
  const lastId = visible.length ? visible[visible.length - 1].msg_id : null;
  useEffect(() => {
    if (!scrollRef.current) return;
    // Use rAF so the DOM has committed the new nodes before we measure.
    requestAnimationFrame(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }
    });
  }, [visible.length, lastId, filter]);

  const send = async (e) => {
    e.preventDefault();
    const t = text.trim();
    if (!t || sending) return;
    // When the filter is "promotion" and the user is in one, post to that
    // scope. Otherwise post globally.
    const scope = filter === "promotion" && inPromotion ? "promotion" : "global";
    setSending(true);
    try {
      const r = await api.post("/chat", { message: t, scope });
      setText("");
      setMessages((prev) => [...prev, r.data]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
      if (err.response?.status === 403) refresh?.();
    } finally {
      setSending(false);
    }
  };

  const openProfile = (accountName) => {
    if (accountName) nav(`/profile/${encodeURIComponent(accountName)}`);
  };

  return (
    <div data-testid="chatbox" className={`relative rounded-2xl p-[2px] ${compact ? "" : "mb-6"}`}
         style={{ background: "linear-gradient(150deg, #00F0FF, #5B3BFF, #FF007F)", boxShadow: "0 0 22px #00F0FF22" }}>
     <div className="relative rounded-[14px] bg-[#110726]/95 backdrop-blur-md px-4 pt-3 pb-4">
      <div className="flex items-center gap-2 mb-2.5 relative">
        <MessageCircle size={20} strokeWidth={2.6} className="text-[#00F0FF]" style={{ filter: "drop-shadow(0 0 6px #00F0FF)" }} />
        <div>
          <h2 className="font-graffiti text-xl uppercase leading-none text-[#00F0FF]" style={{ textShadow: "0 0 12px #00F0FF99, 2px 2px 0 #000" }}>Locker Room Chat</h2>
          <div className="mt-1 h-[2px] rounded-full" style={{ background: "linear-gradient(90deg, #00F0FF, #00F0FF 40%, transparent)", boxShadow: "0 0 6px #00F0FF88" }} />
        </div>
        {/* Filter picker */}
        <button
          data-testid="chat-filter-toggle"
          onClick={() => setFilterOpen(o => !o)}
          title="Filter chat"
          className={`p-1.5 rounded border transition-colors ${filter !== "all" ? "border-[#FFE600] text-[#FFE600] bg-[#FFE600]/10" : "border-[#39194D] text-[#A89BCC] hover:border-[#00F0FF] hover:text-[#00F0FF]"}`}
        >
          <Filter size={14} />
        </button>
        {filter !== "all" && (
          <span data-testid="chat-filter-active" className="text-[10px] font-mono text-[#FFE600] uppercase tracking-widest">
            {filter === "announcer" ? "Announcer" : filter === "player" ? "Players" : "Promotion"}
          </span>
        )}
        <span className="ml-auto text-xs font-mono text-[#695D8E]">{visible.length} msg</span>
        {online != null && (
          <span data-testid="chat-online-count" className="inline-flex items-center gap-1.5 pl-2.5 ml-1 border-l border-[#2D1C52] text-xs font-mono text-[#39FF14]" title="Players online in the last 5 minutes">
            <span className="relative flex w-2.5 h-2.5">
              <span className="absolute inset-0 rounded-full bg-[#39FF14] opacity-70 animate-ping" />
              <span className="relative w-2.5 h-2.5 rounded-full bg-[#39FF14]" style={{ boxShadow: "0 0 8px #39FF14" }} />
            </span>
            <span className="font-num text-sm leading-none">{online}</span> online
          </span>
        )}
        {filterOpen && (
          <div
            data-testid="chat-filter-menu"
            className="absolute top-9 left-56 z-30 rounded-lg border border-[#00F0FF]/60 bg-[#0A051A]/95 backdrop-blur-md p-1 shadow-xl min-w-[160px]"
          >
            {[
              { id: "all", label: "All", Icon: MessageCircle, color: "#00F0FF" },
              { id: "announcer", label: "Ring Announcer", Icon: Megaphone, color: "#FFE600" },
              { id: "player", label: "Player Chat", Icon: UsersIcon, color: "#FF007F" },
              ...(inPromotion ? [{ id: "promotion", label: "Promotion Chat", Icon: Shield, color: "#39FF14" }] : []),
            ].map(({ id, label, Icon, color }) => (
              <button
                key={id}
                data-testid={`chat-filter-${id}`}
                onClick={() => { setFilter(id); setFilterOpen(false); }}
                className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded text-xs font-mono text-left transition-colors ${filter === id ? "bg-[#FF007F]/20 text-white" : "text-[#A89BCC] hover:bg-[#00F0FF]/10"}`}
              >
                <Icon size={14} style={{ color }} />
                {label}
                {filter === id && <span className="ml-auto text-[#39FF14]">✓</span>}
              </button>
            ))}
          </div>
        )}
      </div>

      <div
        ref={scrollRef}
        data-testid="chat-messages"
        className={`bg-[#0A051A]/90 rounded-xl border border-[#2D1C52] shadow-[inset_0_0_18px_rgba(91,59,255,0.18)] px-3 py-2 overflow-y-auto mb-3 ${compact ? "space-y-0.5" : "space-y-1"} pvp-scroll ${compact ? "h-48 lg:h-[clamp(54px,calc(100vh-712px),168px)]" : "h-48"}`}
      >
        {visible.length === 0 && (
          <div className="text-center text-[#695D8E] font-mono text-xs py-8">
            {filter === "all" ? "Be the first to talk trash. The mic is yours." : "No messages match this filter."}
          </div>
        )}
        {visible.map((m) => {
          const isSystem = m.system === true || m.user_id === "__system__";
          const isTitleShot = isSystem && m.meta?.kind === "title_shot";
          const isEventWin = isSystem && (m.meta?.kind === "rumble_win" || m.meta?.kind === "bank_win");
          const isPW = isSystem && isPWCardKind(m.meta?.kind);
          const isPromoScope = (m.scope || "") === "promotion";
          const mine = user && !isSystem && m.user_id === user.user_id;

          if (isTitleShot) {
            return (
              <div key={m.msg_id} data-testid={`chat-msg-${m.msg_id}`}>
                <div className="text-[10px] font-mono text-[#695D8E] mb-0.5">[{formatTime(m.ts)}] Ring Announcer</div>
                <TitleShotCard m={m} onOpenProfile={openProfile} />
              </div>
            );
          }

          if (isPW) {
            return (
              <div key={m.msg_id} data-testid={`chat-msg-${m.msg_id}`}>
                <div className="text-[10px] font-mono text-[#695D8E] mb-0.5">[{formatTime(m.ts)}] Ring Announcer</div>
                <PWChatCard m={m} onOpenProfile={openProfile} />
              </div>
            );
          }

          if (isEventWin) {
            return (
              <div key={m.msg_id} data-testid={`chat-msg-${m.msg_id}`}>
                <div className="text-[10px] font-mono text-[#695D8E] mb-0.5">[{formatTime(m.ts)}] Ring Announcer</div>
                <EventWinnerCard m={m} onOpenProfile={openProfile} />
              </div>
            );
          }

          if (isSystem) {
            return (
              <div key={m.msg_id} data-testid={`chat-msg-${m.msg_id}`} className={`text-sm font-mono leading-snug rounded-md px-1 ${compact ? "py-0" : "py-0.5"} transition-colors hover:bg-[#FFE600]/5`}>
                <span className="text-[#695D8E] text-xs mr-2">[{formatTime(m.ts)}]</span>
                <Megaphone size={12} className="inline -mt-0.5 mr-1 text-[#FFE600]" style={{ filter: "drop-shadow(0 0 3px #FFE600)" }} />
                <span
                  data-testid={`chat-system-name-${m.msg_id}`}
                  className="font-heading text-xs tracking-wider mr-2"
                  style={{ color: "#FFE600", textShadow: "0 0 6px #FFE60077" }}
                >
                  {m.account_name}:
                </span>
                <MessageBody text={m.message} mentions={m.mentions} onOpenProfile={openProfile} />
              </div>
            );
          }

          const clickable = !!m.account_name && m.account_name !== "anon";
          return (
            <div
              key={m.msg_id}
              data-testid={`chat-msg-${m.msg_id}`}
              className={`text-sm font-mono leading-snug rounded-md px-1 ${compact ? "py-0" : "py-0.5"} transition-colors hover:bg-[#00F0FF]/5 ${isPromoScope ? "pl-2 border-l-2 border-[#39FF14] rounded-r bg-[#39FF14]/5" : ""} ${mine ? "bg-[#FF007F]/5" : ""}`}
            >
              <span className="text-[#695D8E] text-xs mr-2">[{formatTime(m.ts)}]</span>
              {clickable && (
                <img src={playerAvatar(m.account_name)} alt="" loading="lazy" data-testid={`chat-avatar-${m.msg_id}`}
                     className="inline-block w-5 h-5 rounded-md mr-1.5 -mt-0.5 object-cover border"
                     style={{ borderColor: mine ? "#FF007F" : "#00F0FF" }}
                     onError={(e) => { e.currentTarget.style.display = "none"; }} />
              )}
              {isPromoScope && (
                <span
                  data-testid={`chat-promo-tag-${m.msg_id}`}
                  title={m.promotion_name || "Promotion"}
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 mr-2 rounded text-[9px] font-heading uppercase tracking-widest"
                  style={{ background: "#39FF1422", color: "#39FF14", border: "1px solid #39FF1466" }}
                >
                  <Shield size={10} /> PROMO
                </span>
              )}
              {clickable ? (
                <button
                  type="button"
                  data-testid={`chat-name-${m.msg_id}`}
                  onClick={() => openProfile(m.account_name)}
                  title={`View ${m.account_name}'s profile`}
                  className={`font-heading text-xs tracking-wider mr-2 hover:underline underline-offset-2 cursor-pointer transition-colors ${mine ? "neon-pink hover:text-[#FFB3D6]" : "neon-cyan hover:text-[#B3F5FF]"}`}
                >
                  {m.account_name}:
                </button>
              ) : (
                <span className={`font-heading text-xs tracking-wider mr-2 ${mine ? "neon-pink" : "neon-cyan"}`}>
                  {m.account_name}:
                </span>
              )}
              <span className="text-[#E5DCFF] break-words">{m.message}</span>
            </div>
          );
        })}
      </div>

      <form onSubmit={send} className="flex gap-2">
        <input
          data-testid="chat-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          maxLength={240}
          disabled={!!chatBan}
          title={chatBan ? `Chat banned ${chatBan.until ? "until " + new Date(chatBan.until).toLocaleString() : "permanently"}${chatBan.reason ? " — " + chatBan.reason : ""}` : undefined}
          placeholder={chatBan ? `Chat banned ${chatBan.until ? "until " + new Date(chatBan.until).toLocaleString() : "permanently"}` : (filter === "promotion" && inPromotion ? "Message your promotion…" : "Type a message...")}
          className={`flex-1 px-3 ${compact ? "py-1" : "py-2"} rounded-xl bg-[#0A051A] border-2 outline-none font-mono text-sm transition-[border-color,box-shadow] focus:shadow-[0_0_12px_#00F0FF55] disabled:opacity-60 disabled:cursor-not-allowed ${filter === "promotion" && inPromotion ? "border-[#39FF14]/60 focus:border-[#39FF14]" : "border-[#FF007F]/40 focus:border-[#00F0FF]"}`}
        />
        <button
          data-testid="chat-send"
          type="submit"
          disabled={sending || !text.trim() || !!chatBan}
          className={`btn-neon text-xs flex items-center gap-2 px-4 ${compact ? "!py-1.5" : ""}`}
        >
          <Send size={14} /> SEND
        </button>
      </form>
     </div>
    </div>
  );
}
